import React, { useEffect, useRef, useState } from 'react';
import adminApi from '../api/client';

export type Resolution = {
  author_ref?: { id: string; role: 'author' | 'original_author' };
  publisher_ref?: { id: string };
};

export function CanonicalPicker({ kind, initialName = '', initialId, onSelect }: {
  kind: 'author' | 'publisher'; initialName?: string; initialId?: string;
  onSelect: (id: string | null, name: string) => void;
}) {
  const [text, setText] = useState(initialName);
  const [selected, setSelected] = useState(initialId || null);
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const sequence = useRef(0);
  useEffect(() => {
    // Changed external values are a new confirmed/draft pair. An unrelated
    // parent render with identical values must not erase an active query.
    sequence.current++;
    setText(initialName); setSelected(initialId || null);
    setRows([]); setError(false); setLoading(false);
  }, [kind, initialName, initialId]);
  useEffect(() => {
    const token = ++sequence.current;
    let active = true;
    setRows([]); setError(false); setLoading(false);
    if (selected || text.trim().length < 2) return;
    const timer = setTimeout(async () => {
      setLoading(true); setError(false);
      try {
        const result = await adminApi.get(`/admin/master/suggestions/${kind}`, { params: { q: text.trim(), limit: 8 } });
        if (active && token === sequence.current) setRows(result.data);
      } catch { if (active && token === sequence.current) setError(true); }
      finally { if (active && token === sequence.current) setLoading(false); }
    }, 350);
    return () => { active = false; clearTimeout(timer); };
  }, [text, kind, selected]);
  return <div className="space-y-1">
    <label className="block text-sm font-semibold">
      Canonical {kind}
      {selected && <span aria-label={`Canonical ${kind} selected`}
        className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        ✓ selected
      </span>}
    </label>
    <input value={text} maxLength={100} onChange={event => {
      sequence.current++; setText(event.target.value); setSelected(null); setRows([]);
      onSelect(null, event.target.value);
    }} className="border rounded px-3 py-2 w-full" placeholder={`Search ${kind} names…`} />
    {loading && <p className="text-xs">Loading…</p>}
    {error && <p role="alert" className="text-xs text-red-600">Names unavailable. Edit the query to retry.</p>}
    {selected && <p className="text-xs text-gray-500">Selected ID: {selected}</p>}
    {rows.map(row => {
      const name = kind === 'author' ? row.name || row.name_sinhala : row.name_en || row.name_si;
      const other = kind === 'author' ? row.name_sinhala : row.name_si;
      return <button key={row.id} type="button" className="block w-full text-left border rounded p-2 text-sm"
        onClick={() => { sequence.current++; setSelected(row.id); setText(name); setRows([]); onSelect(row.id, name); }}>
        {name} {other && other !== name ? ` · ${other}` : ''} · {row.review_state} · {row.id}
      </button>;
    })}
  </div>;
}

export default function CanonicalResolution({ changes, isTranslation, onChange, onValidityChange }: {
  changes: Record<string, any>; isTranslation: boolean; onChange: (value: Resolution) => void;
  onValidityChange?: (valid: boolean) => void;
}) {
  const authorChanged = 'author' in changes || 'original_author' in changes || 'is_translation' in changes || !!changes.author_ref;
  const publisherChanged = 'publisher' in changes || !!changes.publisher_ref;
  const role = isTranslation ? 'original_author' : 'author';
  const [value, setValue] = useState<Resolution>({
    author_ref: changes.author_ref ? { id: changes.author_ref.id, role } : undefined,
    publisher_ref: changes.publisher_ref,
  });
  useEffect(() => {
    onChange(value);
    onValidityChange?.((!authorChanged || !!value.author_ref) && (!publisherChanged || !!value.publisher_ref));
  }, [value, authorChanged, publisherChanged, onChange, onValidityChange]);
  if (!authorChanged && !publisherChanged) return null;
  return <div className="rounded border bg-gray-50 p-3 space-y-3">
    <p className="text-sm">Review directory identity before applying. Pending names need catalogue review in Data Directory. Account claims are separate.</p>
    {authorChanged && <CanonicalPicker kind="author" initialName={changes[role] || ''} initialId={changes.author_ref?.id}
      onSelect={id => setValue(previous => ({ ...previous, author_ref: id ? { id, role } : undefined }))} />}
    {publisherChanged && <CanonicalPicker kind="publisher" initialName={changes.publisher || ''} initialId={changes.publisher_ref?.id}
      onSelect={id => setValue(previous => ({ ...previous, publisher_ref: id ? { id } : undefined }))} />}
  </div>;
}
